import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { Box } from "@mui/material";

// Custom gradient definitions
const gradients = {
  views: { start: "#FFD60A", end: "#FFD60A33" },
  tips: { start: "#00D9FF", end: "#00D9FF33" },
  streams: { start: "#00D9FF", end: "#00D9FF33" },
  follow: { start: "#00D9FF", end: "#00D9FF33" }
};

const AreaGraphRecharts = ({ graph, tab, selectedTime, selectedKey }) => {
  // Transform data for Recharts format
  const transformData = () => {
    if (!graph) return [];
    
    // Handle both graph.data and direct array
    const dataPoints = Array.isArray(graph) ? graph : (graph.data || []);
    
    return dataPoints.map((point) => ({
      date: point.x || point.date,
      value: point.y || point.value || 0,
      formattedDate: formatDate(point.x || point.date)
    }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const data = transformData();
  const gradient = gradients[tab] || gradients.views;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
          }}
        >
          <p style={{ color: "#fff", margin: 0 }}>{payload[0].payload.formattedDate}</p>
          <p style={{ color: gradient.start, margin: "5px 0 0 0" }}>
            {`${tab.charAt(0).toUpperCase() + tab.slice(1)}: ${payload[0].value.toLocaleString()}`}
          </p>
        </Box>
      );
    }
    return null;
  };

  // Y-axis formatter
  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  return (
    <Box sx={{ width: "100%", height: "400px", backgroundColor: "#090909", p: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id={`gradient-${tab}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={gradient.start} stopOpacity={0.8} />
              <stop offset="100%" stopColor={gradient.end} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis 
            dataKey="formattedDate" 
            stroke="#888"
            tick={{ fill: '#888' }}
            tickLine={{ stroke: '#333' }}
          />
          <YAxis 
            stroke="#888"
            tick={{ fill: '#888' }}
            tickLine={{ stroke: '#333' }}
            tickFormatter={formatYAxis}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ color: "#888" }}
            iconType="rect"
          />
          <Area
            type="monotone"
            dataKey="value"
            name={tab.charAt(0).toUpperCase() + tab.slice(1)}
            stroke={gradient.start}
            fillOpacity={1}
            fill={`url(#gradient-${tab})`}
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default AreaGraphRecharts;